import React, { useRef, useState } from "react";
import {
  FlatList,
  StyleSheet,
  Image,
  TouchableOpacity,
  Text,
} from "react-native";
import MessageItem from "./ChatMessageItem";
import withErrorBoundary from "../../utility/auth/withErrorBoundary";
import { useChat } from "../../context/ChatContext";
import { View } from "react-native";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { ChatMessage } from "../../utility/ChatSocket/ChatTypeDefinitions";

interface ChatListProps {
  chatData: any[];
  selectionMode?: boolean;
  setSelectionMode?: any;
  selectedMessages?: any[];
  setSelectedMessages?: any;
}

const ChatList: React.FC<ChatListProps> = ({
  chatData,
  selectionMode,
  setSelectionMode,
  selectedMessages,
  setSelectedMessages,
}) => {
  const {
    userData,
    currentChat,
    deleteMessageForEveryone,
    deleteMessageForUser,
    userDataRef,
    setChatData,
    setChats,
  } = useChat();
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const chatContainerRef = useRef(null);

  const handleDeletePress = () => {
    setSelectionMode(true);
  };

  const handleCheckboxToggle = (messageId) => {
    setSelectedMessages((prevSelected) =>
      prevSelected.includes(messageId)
        ? prevSelected.filter((id) => id !== messageId)
        : [...prevSelected, messageId]
    );
  };

  const onDeleteForEveryone = () => {
    selectedMessages.forEach((messageId) => {
      const message = chatData.find((msg) => msg.messageId === messageId);

      if (message) {
        if (userDataRef.current.username === message.userId) {
          deleteMessageForEveryone(message.conversationId, message.messageId);
          //@ts-ignore
          setChatData((prevChatData: ChatMessage[]) =>
            prevChatData.filter((msg) => msg.messageId !== messageId)
          );

          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.conversationId === message.conversationId &&
              chat.lastMessageId === messageId
                ? {
                    ...chat,
                    lastMessage: "This message was deleted",
                    lastMessageTimestamp: null,
                  }
                : chat
            )
          );
        }
      }
    });

    setDeleteModalVisible(false);
    setSelectionMode(false);
    setSelectedMessages([]);
  };

  const onDeleteForMe = () => {
    selectedMessages.forEach((messageId) => {
      const message = chatData.find((msg) => msg.messageId === messageId);

      if (message) {
        deleteMessageForUser(message.conversationId, message.messageId);
        //@ts-ignore
        setChatData((prevChatData: ChatMessage[]) =>
          prevChatData.filter((msg: any) => msg.messageId !== messageId)
        );

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.conversationId === message.conversationId &&
            chat.lastMessageId === messageId
              ? {
                  ...chat,
                  lastMessage: null,
                  lastMessageTimestamp: null,
                }
              : chat
          )
        );
      }
    });

    setDeleteModalVisible(false);
    setSelectionMode(false);
    setSelectedMessages([]);
  };

  const renderChatMessages = ({ item }: { item: any }) => {
    const isSender = item.userId === userData?.username;

    const messageId = item.messageId;
    const isSelected = selectedMessages.includes(messageId);
    return (
      <TouchableOpacity
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
        }}
        onPress={() => handleCheckboxToggle(messageId)}
        activeOpacity={0.7}
      >
        <View>
          {selectionMode && (
            <View
              style={[
                styles.checkboxContainer,
                isSelected && styles.checkedCheckbox,
              ]}
            >
              {isSelected && (
                <Image
                  source={AppImages.icRightIcon}
                  style={styles.checkmarkIcon}
                />
              )}
            </View>
          )}
        </View>

        <MessageItem
          item={item}
          isSender={isSender}
          currentChat={currentChat}
          userData={userData}
          handleDeletePress={handleDeletePress}
          selectionMode={selectionMode}
          selectedMessages={selectedMessages}
          setSelectedMessages={setSelectedMessages}
        />
      </TouchableOpacity>
    );
  };

  return (
    <View ref={chatContainerRef} style={{ flex: 1 }}>
      <FlatList
        data={chatData}
        inverted
        renderItem={renderChatMessages}
        showsVerticalScrollIndicator={false}
      />
      {selectionMode && (
        <View style={styles.bottomBar}>
          <TouchableOpacity
            onPress={() => setDeleteModalVisible(true)}
            disabled={selectedMessages.length === 0}
          >
            <Image
              source={AppImages.icDelete}
              style={{
                height: 24,
                width: 24,
                resizeMode: "contain",
                opacity: selectedMessages.length === 0 ? 0.5 : 1,
              }}
            />
          </TouchableOpacity>
          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_16,
              {
                color: AppColors.black_121212,
              },
            ]}
          >
            {selectedMessages.length} Selected
          </Text>
        </View>
      )}
      {isDeleteModalVisible && (
        <DeleteConfirmationModal
          isVisible={isDeleteModalVisible}
          selectedMessages={selectedMessages}
          onClose={() => setDeleteModalVisible(false)}
          onDeleteForEveryone={onDeleteForEveryone}
          onDeleteForMe={onDeleteForMe}
          chatContainerRef={chatContainerRef}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  checkboxContainer: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: AppColors.white,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 10,
  },
  checkedCheckbox: {
    backgroundColor: AppColors.green_32CD32,
  },
  checkmarkIcon: {
    width: 12,
    height: 12,
    tintColor: AppColors.white,
  },
  bottomBar: {
    height: 50,
    backgroundColor: AppColors.grayColor_D9D9D9,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    marginLeft: -20,
    marginBottom: -10,
    marginRight: -20,
    marginTop: 20,
  },
});
export default withErrorBoundary(ChatList);
